import { DonneeAnalyseSiteDto } from 'src/app/bo/donneeAnalyse';
import { ReferentielDonneeAnalyse } from 'src/app/bo/referentielDonneeAnalyse';
import { KPI_NO_VALUE_FILL } from '../site-map.constants';
import { KpiScale } from '../site-map.types';

const START_HUE = 300;
const END_HUE = 0;
const SATURATION = 80;
const LIGHTNESS = 50;
const GRADIENT_STOPS = 20;

/** Lit la configuration KPI du site (JSON des 4 KPI de référence) sans lever d'exception sur une valeur vide ou invalide. */
export function parseConfiguredKpiIds(configuration: string | null | undefined): (number | null)[] {
  if (!configuration) return [];
  try {
    const parsed: unknown = JSON.parse(configuration);
    return Array.isArray(parsed) ? parsed.map(id => (typeof id === 'number' ? id : null)) : [];
  } catch {
    return [];
  }
}

/** Valeur numérique d'un KPI ; NaN quand la valeur est vide ou non numérique. */
export function parseKpiValue(value: string | null | undefined): number {
  if (value === null || value === undefined || String(value).trim() === '') return NaN;
  return Number(value);
}

/**
 * Bornes de l'échelle de couleurs : celles du référentiel (la valeur nominale complète une borne manquante) ;
 * une borne encore manquante est prise sur le min/max des valeurs du site, sans écraser la borne configurée.
 * Le référentiel n'est jamais muté.
 */
export function computeKpiScale(kpi: ReferentielDonneeAnalyse, values: DonneeAnalyseSiteDto[]): KpiScale {
  const unit = values.find(value => !!value.unite)?.unite ?? '';
  let min = kpi.valeurMin ?? (kpi.valeurMax != null ? kpi.valeurNominal : null) ?? null;
  let max = kpi.valeurMax ?? (kpi.valeurMin != null ? kpi.valeurNominal : null) ?? null;

  if (min == null || max == null) {
    const numericValues = values.map(value => parseKpiValue(value.valeur)).filter(Number.isFinite);
    if (numericValues.length > 0) {
      min ??= numericValues.reduce((a, b) => Math.min(a, b));
      max ??= numericValues.reduce((a, b) => Math.max(a, b));
    }
  }
  return { min, max, unit };
}

/**
 * Couleur d'un polygone sur le dégradé magenta → rouge ; blanc translucide quand la valeur ou l'échelle est inexploitable.
 * Une échelle réduite à un point (toutes les valeurs identiques, ou bornes inversées) prend le milieu du dégradé.
 */
export function kpiColor(value: number, scale: KpiScale): string {
  if (scale.min == null || scale.max == null || !Number.isFinite(value)) return KPI_NO_VALUE_FILL;
  const range = scale.max - scale.min;
  const ratio = range > 0 ? (Math.min(Math.max(value, scale.min), scale.max) - scale.min) / range : 0.5;
  if (!Number.isFinite(ratio)) return KPI_NO_VALUE_FILL;
  const hue = START_HUE + (END_HUE - START_HUE) * ratio;
  return `hsl(${hue}, ${SATURATION}%, ${LIGHTNESS}%, 0.8)`;
}

/** Dégradé CSS de la jauge KPI, identique au dégradé appliqué aux polygones. */
export function kpiGradientCss(): string {
  const stops = Array.from({ length: GRADIENT_STOPS + 1 }, (_, index) => {
    const ratio = index / GRADIENT_STOPS;
    const hue = START_HUE + (END_HUE - START_HUE) * ratio;
    return `hsl(${hue}, ${SATURATION}%, ${LIGHTNESS}%) ${(ratio * 100).toFixed(1)}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/** Palette des zones : teintes réparties uniformément selon l'ordre croissant des zoneId, donc stable pour un site donné. */
export function buildZonePalette(zoneIds: number[]): Map<number, string> {
  const uniqueIds = [...new Set(zoneIds)].sort((a, b) => a - b);
  return new Map(uniqueIds.map((zoneId, index) => [zoneId, `hsl(${(360 / uniqueIds.length) * index}, 75%, 55%)`]));
}
